/**
 * Authentication event system for decoupling auth actions from side effects
 * Allows clean separation between auth state management and Firebase messaging
 */

export enum AuthEvent {
  USER_LOGGED_IN = "user_logged_in",
  USER_LOGGED_OUT = "user_logged_out",
  ACCOUNT_SWITCHED = "account_switched",
}

/**
 * Simple event emitter for authentication events
 * Enables loose coupling between auth store and other services
 */
class AuthEventEmitter {
  private listeners = new Map<AuthEvent, Array<() => void>>();

  /**
   * Emit an authentication event to all listeners
   */
  emit(event: AuthEvent): void {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach((callback) => {
        try {
          callback();
        } catch (error) {
          console.error(`Error in auth event listener for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Subscribe to an authentication event
   * @returns Unsubscribe function
   */
  on(event: AuthEvent, callback: () => void): () => void {
    let callbacks = this.listeners.get(event);
    if (!callbacks) {
      callbacks = [];
      this.listeners.set(event, callbacks);
    }

    callbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Remove all listeners for an event
   */
  removeAllListeners(event?: AuthEvent): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }

  /**
   * Get the number of listeners for an event (useful for debugging)
   */
  listenerCount(event: AuthEvent): number {
    return this.listeners.get(event)?.length || 0;
  }
}

/**
 * Global auth event emitter instance
 * Import this in components that need to emit or listen to auth events
 */
export const authEventEmitter = new AuthEventEmitter();
